import React, { memo, type RefCallback } from 'react'
import { Accordion } from 'react-bootstrap'
import EmbeddedXrayViewer, {
  type EmbeddedXraySelectedFile,
} from '@components/common/EmbeddedXrayViewer'
import Button from '@components/common/Button/Button.component'
import TreatmentPlanToothChart from './TreatmentPlanToothChart/TreatmentPlanToothChart.component'
import type { IPhases } from '@dataTypes/treatmentPlan.types'
import {
  CHART_XRAY_HANDLE_PX,
  CHART_XRAY_PANE_MAX_PCT,
  CHART_XRAY_PANE_MIN_PCT,
} from '../Dental/useChartXraySplit.hook'
import { getTreatmentPlanChartAccordionClassName } from './useTreatmentPlanChartVerticalSplit.hook'
import { formatPresetToothNumsLabel } from '@utils/tx-plan-tooth-details.utils'

export interface TreatmentPlanDentalChartPanelProps {
  chartVisible: boolean
  setChartVisible: React.Dispatch<React.SetStateAction<boolean>>
  chartHeaderName: string
  activeTabKey: string
  localDrafts: Record<string, boolean>
  editModes: Record<string, boolean>
  hasFormChanges: (tabKey: string) => boolean
  handleResetChart: (tabKey: string) => void
  handleUndo: (tabKey: string) => void
  handleRedo: (tabKey: string) => void
  canUndo: (tabKey: string) => boolean
  canRedo: (tabKey: string) => boolean
  showXraySplit: boolean
  attachTpXraySplitRef: (node: HTMLDivElement | null) => void
  tpXrayPaneFraction: number
  onTpXraySplitHandleMouseDown: (
    event: React.MouseEvent<HTMLDivElement>,
  ) => void
  memoizedFormStatePhases: IPhases[] | null
  phaseVisibility: Record<string, boolean>
  initialEmbeddedFiles?: EmbeddedXraySelectedFile[] | null
  isHostVisible?: boolean
  isChartInteractive: boolean
  chartSelectedTeeth: number[]
  onChartToothToggle: (toothId: number) => void
  onClearChartSelectedTeeth: () => void
  onAddProceduresForSelectedTooth: () => void
  chartPaneHeightPx: number | null
  chartPaneRef: RefCallback<HTMLDivElement>
}

const TreatmentPlanDentalChartPanelInner = ({
  chartVisible,
  setChartVisible,
  chartHeaderName,
  activeTabKey,
  localDrafts,
  editModes,
  hasFormChanges,
  handleResetChart,
  handleUndo,
  handleRedo,
  canUndo,
  canRedo,
  showXraySplit,
  attachTpXraySplitRef,
  tpXrayPaneFraction,
  onTpXraySplitHandleMouseDown,
  memoizedFormStatePhases,
  phaseVisibility,
  initialEmbeddedFiles,
  isHostVisible = true,
  isChartInteractive,
  chartSelectedTeeth,
  onChartToothToggle,
  onClearChartSelectedTeeth,
  onAddProceduresForSelectedTooth,
  chartPaneHeightPx,
  chartPaneRef,
}: TreatmentPlanDentalChartPanelProps): React.ReactElement => {
  const selectedTeethLabel = formatPresetToothNumsLabel(chartSelectedTeeth)
  const isEditMode =
    !!activeTabKey &&
    (Boolean(localDrafts[activeTabKey]) || Boolean(editModes[activeTabKey]))
  const bodyClassName = [
    'treatment-plan-chart-body-content',
    chartPaneHeightPx !== null
      ? 'treatment-plan-chart-body-content--sized'
      : '',
  ]
    .filter(Boolean)
    .join(' ')
  const bodyStyle =
    chartPaneHeightPx !== null
      ? ({
          '--tp-chart-pane-height': `${chartPaneHeightPx}px`,
          height: chartPaneHeightPx,
          maxHeight: chartPaneHeightPx,
        } as React.CSSProperties)
      : undefined

  return (
    <Accordion
      activeKey={chartVisible ? 'chart' : ''}
      onSelect={() => {}}
      className="mb-0 treatment-plan-chart-accordion-sticky"
    >
      <Accordion.Item
        eventKey="chart"
        className={getTreatmentPlanChartAccordionClassName(chartVisible)}
      >
        <Accordion.Header
          as="div"
          className="treatment-plan-chart-header p-0"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <div className="treatment-plan-chart-header-content">
            <div className="d-flex align-items-center gap-2">
              <span className="treatment-plan-chart-header-title">
                {chartHeaderName}
              </span>
              {isEditMode && (
                <div
                  className={`tx-chart-reset-button ${!hasFormChanges(activeTabKey) ? 'disabled pe-none' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (activeTabKey && hasFormChanges(activeTabKey)) {
                      handleResetChart(activeTabKey)
                    }
                  }}
                  title={
                    !hasFormChanges(activeTabKey)
                      ? 'No changes to reset'
                      : 'Reset changes'
                  }
                  style={{
                    cursor: !hasFormChanges(activeTabKey)
                      ? 'not-allowed'
                      : 'pointer',
                  }}
                >
                  <i className="mgc_refresh_anticlockwise_1_line"></i>
                  <span>Reset</span>
                </div>
              )}
            </div>
            <div className="d-flex align-items-center gap-3">
              {isChartInteractive && chartSelectedTeeth.length >= 1 && (
                <div className="d-flex align-items-center treatment-plan-chart-selection-actions">
                  {chartSelectedTeeth.length > 1 && (
                    <Button
                      className="button-secondary treatment-plan-chart-clear-selected-btn"
                      onClick={(e) => {
                        e.stopPropagation()
                        onClearChartSelectedTeeth()
                      }}
                      title="Clear selected teeth"
                    >
                      Clear selected
                    </Button>
                  )}
                  <Button
                    className="button-primary treatment-plan-chart-add-procedures-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      onAddProceduresForSelectedTooth()
                    }}
                    title={
                      chartSelectedTeeth.length > 1
                        ? `Add procedures to ${selectedTeethLabel}`
                        : 'Add procedures to the selected tooth'
                    }
                  >
                    Add procedures
                  </Button>
                </div>
              )}
              {isEditMode && (
                <>
                  <div className="d-flex align-items-center gap-1">
                    <i
                      className={`mgc_back_2_line ${!canUndo(activeTabKey) ? 'disabled pe-none' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (activeTabKey && canUndo(activeTabKey)) {
                          handleUndo(activeTabKey)
                        }
                      }}
                      title={
                        !canUndo(activeTabKey) ? 'Nothing to undo' : 'Undo'
                      }
                      style={{
                        cursor: !canUndo(activeTabKey)
                          ? 'not-allowed'
                          : 'pointer',
                      }}
                    ></i>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <i
                      className={`mgc_forward_2_line ${!canRedo(activeTabKey) ? 'disabled pe-none' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (activeTabKey && canRedo(activeTabKey)) {
                          handleRedo(activeTabKey)
                        }
                      }}
                      title={
                        !canRedo(activeTabKey) ? 'Nothing to redo' : 'Redo'
                      }
                      style={{
                        cursor: !canRedo(activeTabKey)
                          ? 'not-allowed'
                          : 'pointer',
                      }}
                    ></i>
                  </div>
                </>
              )}
              <div className="d-flex align-items-center gap-2">
                <span className="small">Dental chart</span>
                <i
                  className={`icon-wrap ${chartVisible ? 'mgc_eye_2_fill' : 'mgc_eye_close_line'}`}
                  style={{ cursor: 'pointer', fontSize: '18px' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    setChartVisible(!chartVisible)
                  }}
                ></i>
              </div>
            </div>
          </div>
        </Accordion.Header>
        <Accordion.Body className="p-0">
          <div ref={chartPaneRef} className={bodyClassName} style={bodyStyle}>
            <div
              ref={attachTpXraySplitRef}
              className={
                showXraySplit
                  ? 'charting-chart-xray-split d-flex flex-row align-items-stretch w-100'
                  : 'h-100 w-100'
              }
            >
              <div
                className={
                  showXraySplit
                    ? 'charting-chart-xray-split-chart-column d-flex flex-column gap-2 min-h-0'
                    : 'h-100 w-100'
                }
                style={
                  showXraySplit
                    ? {
                        flex: `0 0 ${tpXrayPaneFraction * 100}%`,
                        minWidth: 0,
                      }
                    : undefined
                }
              >
                <TreatmentPlanToothChart
                  activeTreatmentPlanId={
                    activeTabKey &&
                    activeTabKey !== 'create-new' &&
                    !localDrafts[activeTabKey]
                      ? activeTabKey
                      : null
                  }
                  phaseVisibility={phaseVisibility}
                  formStatePhases={memoizedFormStatePhases}
                  layoutMode={showXraySplit ? 'xray-split' : 'full'}
                  fillPane={chartPaneHeightPx !== null}
                  interactive={isChartInteractive}
                  selectedTeeth={chartSelectedTeeth}
                  onToggleTooth={onChartToothToggle}
                />
              </div>
              {showXraySplit ? (
                <>
                  <div
                    role="separator"
                    aria-orientation="vertical"
                    aria-valuenow={Math.round(tpXrayPaneFraction * 100)}
                    aria-valuemin={CHART_XRAY_PANE_MIN_PCT}
                    aria-valuemax={CHART_XRAY_PANE_MAX_PCT}
                    tabIndex={0}
                    className="charting-xray-split-handle"
                    onMouseDown={onTpXraySplitHandleMouseDown}
                    style={{
                      flex: `0 0 ${CHART_XRAY_HANDLE_PX}px`,
                    }}
                  />
                  <div className="charting-xray-split-viewer flex-grow-1 min-w-0 d-flex min-h-0">
                    <EmbeddedXrayViewer
                      initialFiles={initialEmbeddedFiles}
                      isHostVisible={isHostVisible}
                    />
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  )
}

const TreatmentPlanDentalChartPanel = memo(TreatmentPlanDentalChartPanelInner)

export default TreatmentPlanDentalChartPanel
